import { Link } from "react-router-dom";
import { Plus, ShieldCheck } from "lucide-react";
import {
  useDocuments,
  categoryLabel,
  daysUntilExpiry,
  DOCUMENT_CATEGORIES,
  type DocumentRow,
} from "@/hooks/use-documents";
import { EmptyState, SkeletonLoader } from "@/components/system";

/**
 * BW10 — /documents top-level. Card grid grouped by category, with expiry
 * countdowns. Sensitive numbers stay encrypted at rest; this surface only
 * shows non-sensitive fields.
 */

export function Documents() {
  const q = useDocuments();
  const groups = groupBy(q.data ?? [], (d) => d.category);

  return (
    <article aria-labelledby="documents-heading">
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            Wallet
          </p>
          <h1
            id="documents-heading"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-m)",
              margin: "var(--space-2) 0 var(--space-6)",
            }}
          >
            Documents.
          </h1>
        </div>
        <Link
          to="/documents/new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "8px 14px",
            background: "var(--accent)",
            color: "#0a0b0d",
            border: "none",
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          <Plus size={14} strokeWidth={2} /> Add document
        </Link>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={3} height={120} />
      ) : !q.data || q.data.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={24} strokeWidth={1.5} />}
          headline="No documents yet."
          sub="Add your passport, visa, flight tickets, gym card, or anything else worth keeping handy."
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--space-6)" }}>
          {DOCUMENT_CATEGORIES.map((cat) => {
            const docs = groups.get(cat.value) ?? [];
            if (docs.length === 0) return null;
            return (
              <section key={cat.value} aria-label={cat.label}>
                <h2
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--type-meta)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--fg-3)",
                    margin: "0 0 var(--space-3)",
                  }}
                >
                  {cat.label} <span style={{ color: "var(--fg-4)" }}>· {docs.length}</span>
                </h2>
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "var(--space-3)",
                  }}
                >
                  {docs.map((d) => (
                    <li key={d.id}>
                      <DocumentCard d={d} />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}

function DocumentCard({ d }: { d: DocumentRow }) {
  const days = daysUntilExpiry(d.expires_at);
  const expiringSoon = days != null && days >= 0 && days <= 90;
  const expired = days != null && days < 0;
  const accent = expired ? "var(--brick)" : expiringSoon ? "var(--amber)" : "var(--hair)";

  return (
    <Link
      to={`/documents/${d.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: `1px solid ${accent}`,
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
        minHeight: 110,
      }}
    >
      <span
        style={{
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {categoryLabel(d.category)} · {d.kind.replace(/_/g, " ")}
      </span>
      <span
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-body-l)",
          color: "var(--fg)",
        }}
      >
        {d.title}
      </span>
      {d.issuer && (
        <span style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>{d.issuer}</span>
      )}
      <footer
        style={{
          marginTop: "auto",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
        }}
      >
        <span>{d.issued_country ?? ""}</span>
        {d.expires_at ? (
          <span
            className="mono"
            style={{
              color: expired ? "var(--brick)" : expiringSoon ? "var(--amber)" : "var(--fg-3)",
            }}
          >
            {expired ? "expired" : `T−${days}d`}
          </span>
        ) : null}
      </footer>
    </Link>
  );
}

function groupBy<T, K>(items: T[], key: (t: T) => K): Map<K, T[]> {
  const m = new Map<K, T[]>();
  for (const it of items) {
    const k = key(it);
    const list = m.get(k) ?? [];
    list.push(it);
    m.set(k, list);
  }
  return m;
}
